import axios from 'axios';
import {
  ApiAsset,
  AssetType,
  AssetListResponse,
  AssetFormUnion,
  AssetFormMapper,
  FormValidator,
  NORWEGIAN_FORM_CONFIGS
} from './asset-models';

import { getApiConfig, buildUrl, API_HEADERS } from '../../apis/api-config';
import { getStorage } from '../../hooks/useAuth';

// Get environment-specific API configuration
const config = getApiConfig();

const apiClient = axios.create({
  baseURL: config.BASE_URL,
  timeout: config.TIMEOUT,
  headers: API_HEADERS,
});

// Add request interceptor for authentication
apiClient.interceptors.request.use((config) => {
  const token = getStorage().getItem('access_token'); // Using your existing token key
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);

    if (error.response?.status === 401) {
      getStorage().removeItem('access_token');
      // Redirect to login if needed
      window.location.href = '/welcome';
    }

    return Promise.reject(error);
  }
);

// Asset Type Service
export class AssetTypeService {
  // Get all asset types
  static async getAssetTypes(): Promise<AssetType[]> {
    try {
      const response = await apiClient.get(config.ENDPOINTS.ASSET_TYPES);

      // Log the actual response to debug
      console.log('Asset Types API Response:', response.data);

      // Your API returns a direct array, so we just need to extract id and name
      if (Array.isArray(response.data)) {
        const assetTypes = response.data.map((item: any) => ({
          id: item.id,
          name: item.name,
          assetSystemType: item.assetSystemType
        }));
        console.log('Mapped asset types:', assetTypes);
        return assetTypes;
      } else {
        console.warn('Expected array but got:', typeof response.data, response.data);
        return [];
      }
    } catch (error: any) {
      console.error('Error fetching asset types:', error);
      console.error('Error response:', error.response?.data);
      throw new Error(`Failed to load asset types: ${error.message}`);
    }
  }

  // Get asset type by ID
  static async getAssetType(id: number): Promise<AssetType> {
    try {
      const url = buildUrl(config.ENDPOINTS.ASSET_TYPE_BY_ID, { id: id.toString() });
      const response = await apiClient.get(url);
      return response.data; // Remove .data.data
    } catch (error) {
      console.error('Error fetching asset type:', error);
      throw error;
    }
  }

}

// Asset Service
export class AssetService {
  // Get all assets with optional filtering
  static async getAssets(filters?: {
    parentId?: number;
    assetTypeId?: number;
    tag?: string;
    search?: string;
    page?: number;
    pageSize?: number;
  }): Promise<AssetListResponse> {
    try {
      const params = new URLSearchParams();

      if (filters?.parentId) params.append('parentId', filters.parentId.toString());
      if (filters?.assetTypeId) params.append('assetTypeId', filters.assetTypeId.toString());
      if (filters?.tag) params.append('tag', filters.tag);
      if (filters?.search) params.append('search', filters.search);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.pageSize) params.append('pageSize', filters.pageSize.toString());

      const url = `${config.ENDPOINTS.ASSETS}${params.toString() ? '?' + params : ''}`;
      const response = await apiClient.get(url);


      // Your API returns a direct array (like asset types)
      if (Array.isArray(response.data)) {
        return {
          data: response.data,
          total: response.data.length,
          page: filters?.page || 1,
          pageSize: filters?.pageSize || response.data.length,
          success: true
        };
      } else {
        console.warn('Expected array but got:', typeof response.data);
        return {
          data: [],
          total: 0,
          page: 1,
          pageSize: 10,
          success: false
        };
      }
    } catch (error) {
      console.error('Error fetching assets:', error);
      throw error;
    }
  }

  // Get asset by ID
  static async getAsset(id: number): Promise<ApiAsset> {
    try {
      const url = buildUrl(config.ENDPOINTS.ASSET_BY_ID, { id: id.toString() });
      const response = await apiClient.get(url);
      return response.data; // Remove .data.data
    } catch (error) {
      console.error('Error fetching asset:', error);
      throw error;
    }
  }

  static async loadAssetsWithDetails(options?: {
    includeFullDetails?: boolean;
    pageSize?: number;
    page?: number;
    filters?: {
      parentId?: number;
      assetTypeId?: number;
      tag?: string;
      search?: string;
    };
  }): Promise<ApiAsset[]> {
    try {
      console.log('Loading assets with details...');

      const {
        includeFullDetails = true,
        pageSize = 100,
        page = 1,
        filters = {}
      } = options || {};

      // Get the basic asset list first
      const response = await this.getAssets({
        ...filters,
        page,
        pageSize
      });

      const basicAssets = response.data || [];
      console.log(`Found ${basicAssets.length} basic assets`);

      if (!includeFullDetails) {
        console.log('Returning basic assets without full details');
        return basicAssets;
      }

      // Load full details for each asset
      console.log('Loading full details for each asset...');
      const fullAssets = await Promise.all(
        basicAssets.map(async (asset, index) => {
          try {
            console.log(`Loading details for asset ${index + 1}/${basicAssets.length}: ${asset.name || asset.id}`);
            const fullAsset = await this.getAsset(asset.id);
            return fullAsset;
          } catch (error) {
            console.error(`Failed to load details for asset ${asset.id} (${asset.name}):`, error);
            // Return basic asset if detailed fetch fails
            return asset;
          }
        })
      );

      console.log('Successfully loaded full asset details');
      return fullAssets;

    } catch (error) {
      console.error('Error in loadAssetsWithDetails:', error);
      throw error;
    }
  }

  static async loadAssetsForParentSelection(): Promise<ApiAsset[]> {
    return this.loadAssetsWithDetails({
      includeFullDetails: true,
      pageSize: 100, // Load more assets for parent selection
      page: 1
    });
  }

  // Create asset using simple form
  // Enhanced createAssetFromForm method with debugging
  static async createAssetFromForm(
    simpleForm: AssetFormUnion,
    formType: string
  ): Promise<ApiAsset> {
    try {
      

      // Validate the simple form first
      const formConfig = NORWEGIAN_FORM_CONFIGS[formType] || [];
      console.log('Form config:', formConfig);

      const validation = FormValidator.validateForm(simpleForm, formConfig);
      console.log('Validation result:', validation);

      if (!validation.isValid) {
        throw new Error(`Form validation failed: ${validation.errors.join(', ')}`);
      }

      console.log('✅ Form validation passed');
      const apiRequest = AssetFormMapper.mapToApiRequest(simpleForm, formType);
      const response = await apiClient.post(config.ENDPOINTS.ASSETS, apiRequest);

      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  static async updateAsset(id: number, assetData: Partial<ApiAsset>): Promise<ApiAsset> {
    try {
      console.log('🔄 AssetService.updateAsset called');
      console.log('Asset ID:', id);
      console.log('Asset data to update:', assetData);
      console.log('Asset data JSON:', JSON.stringify(assetData, null, 2));

      // Build the URL using the same pattern as getAsset and deleteAsset
      const url = buildUrl(config.ENDPOINTS.ASSET_BY_ID, { id: id.toString() });

      console.log('📡 API endpoint:', url);
      console.log('📤 Sending PUT request to API...');

      // Send PUT request to update the asset
      const response = await apiClient.put(url, assetData);

      console.log('📥 API response:', response);
      console.log('📥 Response data:', response.data);
      console.log('✅ Asset updated successfully');

      return response.data;
    } catch (error: any) {
      console.error('❌ Error in updateAsset:');
      console.error('Error type:', typeof error);
      console.error('Error:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      console.error('Error response headers:', error.response?.headers);

      // Re-throw with more context if possible
      if (error.response?.data?.message) {
        throw new Error(`Failed to update asset: ${error.response.data.message}`);
      } else if (error.response?.status) {
        throw new Error(`Failed to update asset: HTTP ${error.response.status}`);
      } else {
        throw new Error(`Failed to update asset: ${error.message || 'Unknown error'}`);
      }
    }
  }

  // Delete asset
  static async deleteAsset(id: number): Promise<void> {
    try {
      const url = buildUrl(config.ENDPOINTS.ASSET_BY_ID, { id: id.toString() });
      await apiClient.delete(url);
    } catch (error) {
      console.error('Error deleting asset:', error);
      throw error;
    }
  }


  // Get child assets
  static async getChildAssets(parentId: number): Promise<ApiAsset[]> {
    try {
      const response = await this.getAssets({ parentId });
      return response.data;
    } catch (error) {
      console.error('Error fetching child assets:', error);
      throw error;
    }
  }

  // Get assets by type
  static async getAssetsByType(assetTypeId: number): Promise<ApiAsset[]> {
    try {
      const response = await this.getAssets({ assetTypeId });
      return response.data;
    } catch (error) {
      console.error('Error fetching assets by type:', error);
      throw error;
    }
  }


}


// Utility functions
export class AssetUtils {
  // Format asset for display in lists/cards
  static formatAssetForDisplay(asset: any): {
    title: string;
    subtitle: string;
    details: string[];
    type: string;
    distance?: number;
    coordinates?: number[];
  } {
    // Use the actual properties from your API response
    const title = asset?.name || 'Unnamed Asset';
    const type = asset?.assetType?.name || asset?.type?.name || 'Unknown Type';
    const subtitle = asset?.description || type;
    const details: string[] = [];
    const coordinates = asset?.position?.coordinates || [];

    // Add asset type info
    if (asset?.assetType?.name) {
      details.push(`Type: ${asset.assetType.name}`);
    }

    // Add parent info if available
    if (asset?.parentId) {
      details.push(`Parent ID: ${asset.parentId}`);
    }

    // Add position info if available
    if (asset?.position?.coordinates) {
      const coords = asset.position.coordinates;
      details.push(`Location: ${coords[1]?.toFixed(4)}, ${coords[0]?.toFixed(4)}`);
    }

    // Add active status
    if (asset?.isActive !== undefined) {
      details.push(`Status: ${asset.isActive ? 'Active' : 'Inactive'}`);
    }

    return {
      title,
      subtitle,
      details,
      type,
      coordinates: coordinates.length > 0 ? coordinates : undefined,
    };
  }
}

// Export all services for easy importing
export default {
  AssetTypeService,
  AssetService,
  AssetUtils
};